import { useQueries } from '@tanstack/react-query'
import { Constants } from 'constants/constants'
import { QueryKeys } from 'constants/query-keys'
import { TIME } from 'utils/misc'
import { useAnalytics } from 'utils/analytics'
import { fetchMyTargetPerformanceGreenStores } from 'queries/myTarget-performance/performance-metrics'
import { MPMEnvType } from 'utils/env'
import { getGreenfieldLocationFilter } from 'utils/greenfield'
import { isCompany, isRegion, isGroup } from 'utils/location'
import { TableData } from 'utils/data'

const { MY_TARGET_PERFORMANCE } = Constants.GREENFIELD_CARDS

/**
 * Returns the two green stores card numbers needed for the detail page
 * based on the current location level.
 *
 * Company → [company, region]
 * Region  → [region, group]
 * Group   → [group, district]
 * District/Store → [] (no calls needed)
 */
export const getDetailGreenStoresCards = (location: string): string[] => {
    if (isCompany(location) || !location) {
        return [
            MY_TARGET_PERFORMANCE.GREEN_STORES_COMPANY,
            MY_TARGET_PERFORMANCE.GREEN_STORES_REGION,
        ]
    }
    if (isRegion(location)) {
        return [
            MY_TARGET_PERFORMANCE.GREEN_STORES_REGION,
            MY_TARGET_PERFORMANCE.GREEN_STORES_GROUP,
        ]
    }
    if (isGroup(location)) {
        return [
            MY_TARGET_PERFORMANCE.GREEN_STORES_GROUP,
            MY_TARGET_PERFORMANCE.GREEN_STORES_DISTRICT,
        ]
    }
    return []
}

interface UseDetailGreenStoresParams {
    env: MPMEnvType
    accessToken: string
    location: string
    enabled?: boolean
}

interface UseDetailGreenStoresResult {
    greenStoresData: Record<string, TableData | undefined>
    isLoading: boolean
    isError: boolean
    refetch: () => void
}

export function useMyTargetPerformanceDetailGreenStores({
    env,
    accessToken,
    location,
    enabled = true,
}: UseDetailGreenStoresParams): UseDetailGreenStoresResult {
    const { trackEvent } = useAnalytics()
    const { tenMinutes } = TIME

    const cardNumbers = getDetailGreenStoresCards(location)
    const shouldFetch = enabled && cardNumbers.length > 0

    const filters = getGreenfieldLocationFilter(
        isCompany(location) ? '' : location,
    )

    const queries = cardNumbers.map((cardNumber) => ({
        queryKey: [
            QueryKeys.MY_TARGET_PERFORMANCE_DETAIL_GREEN_STORES,
            cardNumber,
            location,
        ],
        queryFn: () =>
            fetchMyTargetPerformanceGreenStores({
                env,
                accessToken,
                dynamicSubPath: cardNumber,
                method: 'GET',
                filters,
                trackEvent,
            }),
        staleTime: tenMinutes,
        cacheTime: tenMinutes,
        retry: false,
        enabled: shouldFetch,
    }))

    const results = useQueries({ queries })

    const greenStoresData = results.reduce(
        (acc, result, index) => {
            acc[cardNumbers[index]] = result.data
            return acc
        },
        {} as Record<string, TableData | undefined>,
    )

    const isLoading = shouldFetch && results.some((result) => result.isLoading)
    const isError = results.some((result) => result.isError)
    const refetch = () => {
        results.forEach((result) => result.refetch())
    }

    return { greenStoresData, isLoading, isError, refetch }
}
